import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Organization, LoginRequest, RegisterRequest, AuthResponse } from '@/types';
import { apiClient } from '@/services/api';

interface AuthState {
  user: User | null;
  organization: Organization | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  clearError: () => void;
  loadUser: () => Promise<void>;
  updateOrganization: (data: Partial<Organization>) => void;
}

type AuthStore = AuthState & AuthActions;

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      organization: null,
      token: null,
      isLoading: false,
      error: null,
      isAuthenticated: false,

      // Actions
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        
        try {
          const response: AuthResponse = await apiClient.login(
            credentials.email,
            credentials.password
          );

          const { user, organization, token } = response;

          // Store token in localStorage for API client
          localStorage.setItem('auth_token', token);

          set({
            user,
            organization,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (data: RegisterRequest) => {
        set({ isLoading: true, error: null });

        try {
          const response: AuthResponse = await apiClient.register({
            email: data.email,
            password: data.password,
            organizationName: data.organizationName,
            facilityType: data.facilityType,
          });

          const { user, organization, token } = response;

          // Store token in localStorage for API client
          localStorage.setItem('auth_token', token);

          set({
            user,
            organization,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

        } catch (error: any) {
          set({
            error: error.message || 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await apiClient.logout();
        } catch (error) {
          // Even if logout fails on server, clear local state
          console.error('Logout error:', error);
        } finally {
          // Clear localStorage
          localStorage.removeItem('auth_token');

          // Clear store state
          set({
            user: null,
            organization: null,
            token: null,
            isAuthenticated: false,
            error: null,
          });
        }
      },

      sendPasswordReset: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          await apiClient.sendPasswordReset(email);
          set({
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to send password reset email',
            isLoading: false,
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      loadUser: async () => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          return;
        }

        set({ isLoading: true });

        try {
          // Use Firebase API client directly instead of backend API
          const userData = await apiClient.getCurrentUser();
          set({
            user: userData.user,
            organization: userData.organization,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          // Token is invalid, clear it
          localStorage.removeItem('auth_token');
          set({
            user: null,
            organization: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      updateOrganization: (data: Partial<Organization>) => {
        const { organization } = get();
        if (organization) {
          set({
            organization: { ...organization, ...data }
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        organization: state.organization,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;